import {
  CHAT_ERROR_CODES,
  type ChatErrorCode,
  getChatErrorCode,
  isChatErrorOf,
} from '@/api/chat/error-codes';
import { TOPIC_LABEL_MAX_LENGTH } from '@/api/chat/v2-types';

import type { TopicColor } from '../components';
import {
  CONVERSATION_TOPIC_LOCKED_REASON,
  NEW_THREAD_BLOCKED_REASON,
  RECIPIENT_NOT_IN_DIRECTORY_REASON,
  SELF_RECIPIENT_REASON,
  TOPIC_NAME_RESERVED_REASON,
} from '../constants';
import type { TopicRecord } from '../types';

export const activeTopics = (topics: TopicRecord[]): TopicRecord[] =>
  topics.filter((topic) => !topic.archivedAt);

const RESERVED_SYSTEM_TOPIC_NAMES = [
  'Inquiry',
  'Gamma',
  'Individual',
  'Entity',
  'Deal',
  'Fund',
  'Event',
  'Support',
];

export const validateTopicName = (
  name: string,
  topics: TopicRecord[],
  { excludeId, isDesk = false }: { excludeId?: string; isDesk?: boolean } = {},
): string | null => {
  const trimmed = name.trim();
  if (!trimmed) return 'Give the topic a name.';
  if (trimmed.length > TOPIC_LABEL_MAX_LENGTH)
    return `Keep it under ${TOPIC_LABEL_MAX_LENGTH} characters.`;

  if (
    isDesk &&
    RESERVED_SYSTEM_TOPIC_NAMES.some((reserved) => reserved.toLowerCase() === trimmed.toLowerCase())
  )
    return TOPIC_NAME_RESERVED_REASON;

  const clash = topics.some(
    (topic) =>
      !topic.archivedAt &&
      topic.id !== excludeId &&
      topic.label.trim().toLowerCase() === trimmed.toLowerCase(),
  );

  return clash ? 'You already have a topic with that name.' : null;
};

const START_REFUSAL_REASONS: Partial<Record<ChatErrorCode, string>> = {
  [CHAT_ERROR_CODES.threadCreateForbidden]: NEW_THREAD_BLOCKED_REASON,
  [CHAT_ERROR_CODES.recipientNotPermitted]: RECIPIENT_NOT_IN_DIRECTORY_REASON,
  [CHAT_ERROR_CODES.recipientInvalid]: SELF_RECIPIENT_REASON,
  [CHAT_ERROR_CODES.recipientInvalidDesk]: RECIPIENT_NOT_IN_DIRECTORY_REASON,
};

export const conversationStartError = (
  error: unknown,
  fallback: string,
  lockedReason: string = CONVERSATION_TOPIC_LOCKED_REASON,
): string => {
  if (isChatErrorOf(error, CHAT_ERROR_CODES.topicLocked)) return lockedReason;

  const code = getChatErrorCode(error);

  return (code && START_REFUSAL_REASONS[code]) ?? fallback;
};

export type TopicEdit =
  | { type: 'create'; id: string; label: string; color: TopicColor; visibleToRoleIds?: string[] }
  | { type: 'rename'; id: string; label: string }
  | { type: 'recolour'; id: string; color: TopicColor }
  | { type: 'archive'; id: string; at: string }
  | { type: 'restore'; id: string };

export const applyTopicEdit = (topics: TopicRecord[], edit: TopicEdit): TopicRecord[] => {
  if (edit.type === 'create') {
    return [
      ...topics,
      {
        id: edit.id,
        label: edit.label.trim(),
        color: edit.color,
        archivedAt: null,
        ...(edit.visibleToRoleIds ? { visibleToRoleIds: edit.visibleToRoleIds } : {}),
      },
    ];
  }

  return topics.map((topic) => {
    if (topic.id !== edit.id) return topic;

    switch (edit.type) {
      case 'rename':
        return { ...topic, label: edit.label };
      case 'recolour':
        return { ...topic, color: edit.color };
      case 'archive':
        return topic.isSystem ? topic : { ...topic, archivedAt: edit.at };
      case 'restore':
        return { ...topic, archivedAt: null };
    }
  });
};
